<template>
	<div class="plugin-manager">
		<div v-if="!showCreatePage">
			<el-input v-model="searchQuery" placeholder="搜索插件名称" prefix-icon="el-icon-search" size="small" class="mb-15"
				clearable @clear="fetchList">
				<el-button slot="append" @click="fetchList">搜索</el-button>
			</el-input>

			<div class="toolbar flex-between mb-10">
				<div class="flex-center">
					<el-button type="primary" size="small" @click="handleCreate">创建插件</el-button>
					<el-button size="small" class="ml-10">回收站</el-button>
					<el-dropdown class="ml-10">
						<el-button size="small" disabled>批量操作<i class="el-icon-more"></i></el-button>
						<el-dropdown-menu slot="dropdown">
							<el-dropdown-item>批量启用</el-dropdown-item>
						</el-dropdown-menu>
					</el-dropdown>
					<el-link type="primary" class="ml-20" :underline="false">如何使用插件?</el-link>
				</div>

				<div class="flex-center pagination-wrap">
					<span class="f-13 gray">总记录数: {{ total }}</span>
					<el-pagination layout="prev, slot, next" :total="total" :page-size="20" size="small"
						@current-change="handlePageChange">
						<span class="page-slot">第 {{ currentPage }} 页</span>
					</el-pagination>
				</div>
			</div>

			<el-table :data="tableData" v-loading="loading" class="custom-table" size="medium" style="width: 100%">
				<el-table-column type="selection" width="55" fixed="left"></el-table-column>

				<el-table-column label="启用" width="70">
					<template slot-scope="{row}">
						<el-switch v-model="row.status" :active-value="1" :inactive-value="0"
							@change="handleStatusChange(row)"></el-switch>
					</template>
				</el-table-column>

				<el-table-column label="插件名称" prop="name" min-width="120"></el-table-column>

				<el-table-column label="基本信息" min-width="160">
					<template slot-scope="{row}">
						<div class="flex-center">
							<el-avatar :size="24" :src="row.logo" class="mr-10">
								<i class="el-icon-user-solid"></i>
							</el-avatar>
							<span>{{ row.title || '-' }}</span>
						</div>
					</template>
				</el-table-column>

				<el-table-column label="语言" prop="lang" width="80"></el-table-column>

				<el-table-column label="安装代码" width="100">
					<template slot-scope="{row}">
						<el-button size="mini" plain type="primary" :loading="row.copyLoading"
							@click="handleCopyCode(row)">
							复制
						</el-button>
					</template>
				</el-table-column>

				<el-table-column label="创建时间" width="160">
					<template slot-scope="{row}">
						{{ formatTime(row.createtime) }}
					</template>
				</el-table-column>

				<el-table-column label="操作" width="220" fixed="right">
					<template slot-scope="{row}">
						<div class="ops-container">
							<span class="ops-grid" @click="handleEdit(row)">编辑插件</span>
							<span class="ops-grid" @click="handlePreview(row)">预览</span>
							<span class="ops-grid" @click="handleLink(row)">专属链接</span>
							<span class="ops-grid delete" @click="handleDelete(row)">删除</span>
						</div>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<plugin-config v-else :edit-data="currentEditData" @back="handleBack" @refresh="fetchList" />
	</div>
</template>

<script>
	import plugin from './plugin.vue';

	export default {
		components: {
			'plugin-config': plugin
		},
		data() {
			return {
				searchQuery: '',
				total: 0,
				currentPage: 1,
				loading: false,
				showCreatePage: false,
				currentEditData: null, // 存储当前正在编辑的行数据
				tableData: []
			};
		},
		created() {
			this.fetchList();
		},
		methods: {
			// 获取列表数据
			async fetchList() {
				this.loading = true;
				try {
					const params = {
						page: this.currentPage,
						search: this.searchQuery
					};
					const res = await this.$axios.get(this.$apis.getChannelList, {
						params
					});
					if (res.code === 1 && res.data) {
						this.tableData = res.data.list || [];
						this.total = res.data.total || 0;
					}
				} catch (error) {
					this.$message.error("获取数据失败");
				} finally {
					this.loading = false;
				}
			},


			handleCreate() {
				this.currentEditData = null;
				this.showCreatePage = true;
			},

			// 点击编辑
			handleEdit(row) {

				this.currentEditData = JSON.parse(JSON.stringify(row));
				this.showCreatePage = true;
			},

			// 点击删除
			handleDelete(row) {
				this.$confirm('确定要删除该插件吗？删除后不可恢复', '提示', {
					confirmButtonText: '确定',
					cancelButtonText: '取消',
					type: 'warning'
				}).then(async () => {
					try {
						const res = await this.$axios.post(this.$apis.delChannel, {
							ids: row.id
						});
						if (res.code === 1) {
							this.$message.success('删除成功');
							this.fetchList();
						} else {
							this.$message.error(res.msg || '删除失败');
						}
					} catch (e) {
						this.$message.error('请求异常');
					}
				}).catch(() => {});
			},

			// 返回列表
			handleBack() {
				this.showCreatePage = false;
				this.currentEditData = null;
			},

			async handleStatusChange(row) {
				// 1. 准备 form-data 格式的数据
				const formData = new FormData();
				formData.append('id', row.id);
				formData.append('status', row.status);

				try {
					// 2. 发送请求
					const res = await this.$axios.post(this.$apis.changeStatus, formData);

					if (res.code === 1) {
						this.$message.success('状态更新成功');
						this.fetchList();
					} else {
						row.status = row.status === 1 ? 0 : 1;
						this.$message.error(res.msg || '更新失败');
					}
				} catch (e) {
					row.status = row.status === 1 ? 0 : 1;
					this.$message.error('请求异常');
				}
			},

			// 复制安装代码
			async handleCopyCode(row) {
				let copyText = row.link;
				if (!copyText) {
					this.$set(row, 'copyLoading', true);
					try {
						const res = await this.$axios.post(this.$apis.buildDiyJs, {
							channel_id: row.id
						});
						if (res.code === 1 && res.data.url) {
							copyText = res.data.url;
							row.link = res.data.url;
						}
					} catch (error) {
						this.$message.error("生成失败");
						return;
					} finally {
						row.copyLoading = false;
					}
				}
				this.executeCopy(copyText);
			},

			executeCopy(text) {
				const input = document.createElement('textarea');
				input.value = text;
				document.body.appendChild(input);
				input.select();
				try {
					document.execCommand('copy');
					this.$message.success("代码已复制到剪贴板");
				} catch (err) {
					this.$message.error("复制失败");
				}
				document.body.removeChild(input);
			},

			handlePageChange(val) {
				this.currentPage = val;
				this.fetchList();
			},

			formatTime(timestamp) {
				if (!timestamp) return '-';
				const date = new Date(timestamp * 1000);
				return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
			},

			async handlePreview(row) {
				let jsUrl = row.link;
				if (!jsUrl) {
					this.$set(row, 'copyLoading', true);
					try {
						const res = await this.$axios.post(this.$apis.buildDiyJs, {
							channel_id: row.id
						});
						if (res.code === 1 && res.data.url) {
							jsUrl = res.data.url;
							row.link = res.data.url;
						} else {
							this.$message.error("无法生成预览地址");
							return;
						}
					} catch (error) {
						this.$message.error("获取失败");
						return;
					} finally {
						row.copyLoading = false;
					}
				}

				this.executeCopy(jsUrl);

				const newWindow = window.open('', '_blank');
				if (!newWindow) return;

				const startTag = '<script>';
				const endTag = '</' + 'script>';

				const htmlContent = `
			        <!DOCTYPE html>
			        <html>
			        <head>
			            <title>预览 - ${row.name || ''}</title>
			            <meta charset="utf-8">
			            <style>
			                html, body {
			                    margin: 0; padding: 0; width: 100%; height: 100%;
			                    background-color: #f7f8fa; overflow: hidden;
			                    display: flex; align-items: center; justify-content: center;
			                    font-family: sans-serif;
			                }
			
			                /* 容器：包裹文字和插件 */
			                .preview-wrapper {
			                    display: flex;
			                    align-items: center;
			                    width: 100%;
			                    max-width: 1200px; /* 限制最大宽度，防止字离插件太远 */

			                }
			
			                /* 左侧文字部分 */
			                .notice-side {
			                    flex: 1; /* 占据剩余空间 */
			                    color: #1a45b8;
			                    font-size: 26px;
			                    font-weight: 500;
			                    white-space: nowrap;
			                    padding-right: 100px; /* 这里可以控制文字和插件之间的距离 */
			                    text-align: right; /* 让文字向右靠，靠近插件 */
			                }
			
			                /* 右侧插件容器部分 */
			                .plugin-side {
			                    flex: 1; 
			                    display: flex;
			                    justify-content: flex-start; /* 插件靠左，紧贴文字 */
								 padding-right: 100px;
			                }
			
			                /* 强制插件样式重置 */
			                div[id*="chat"], div[class*="chat"], .kefu-container, iframe {
			                    position: static !important;
			                    inset: auto !important;
			                    margin: 0 !important; /* 取消居中，由父级控制 */
			                    display: block !important;
			                    transform: none !important;
			                    min-width: 350px !important;
			                    max-width: 450px !important;
			                    box-shadow: 0 12px 48px rgba(0,0,0,0.15) !important;
			                    border-radius: 12px !important;
			                }
			            </style>
			        </head>
			        <body>
			            <div class="preview-wrapper">
			                <div class="notice-side">
			                    插件预览页面临时有效，请勿用于生产
			                </div>
			                <div class="plugin-side" id="plugin-target">
			                    </div>
			            </div>
			
			            ${startTag}
			                (function() {
			                    var script = document.createElement('script');
			                    script.src = '${jsUrl}';
			                    script.async = true;
			                    script.onload = function() {
			                        setTimeout(function() {
			                            // 尝试找到插件元素并移动到 plugin-side 容器内 (如果它没自动进去)
			                            var chatEl = document.querySelector('div[id*="chat"], div[class*="chat"], .kefu-container');
			                            if (chatEl && chatEl.parentNode !== document.getElementById('plugin-target')) {
			                                document.getElementById('plugin-target').appendChild(chatEl);
			                            }
			                            
			                            // 再次强制静态定位
			                            if(chatEl) {
			                                chatEl.style.setProperty('position', 'static', 'important');
			                                chatEl.style.setProperty('inset', 'auto', 'important');
			                            }
			                        }, 500);
			                    };
			                    document.body.appendChild(script);
			                })();
			            ${endTag}
			        </body>
			        </html>
			    `;

				newWindow.document.write(htmlContent);
				newWindow.document.close();
			},
			handleLink(row) {
				console.log('专属链接', row);
			}
		}
	};
</script>

<style lang="scss" scoped>
	.plugin-manager {
		padding: 15px;
		background: #fff;
		min-height: 100%;
	}

	.toolbar {
		border-bottom: 1px solid #ebeef5;
		padding-bottom: 10px;
	}

	.flex-between {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.flex-center {
		display: flex;
		align-items: center;
	}

	.ml-10 {
		margin-left: 10px;
	}

	.ml-20 {
		margin-left: 20px;
	}

	.mr-10 {
		margin-right: 10px;
	}

	.mb-15 {
		margin-bottom: 15px;
	}

	.mb-10 {
		margin-bottom: 10px;
	}

	.f-13 {
		font-size: 13px;
	}

	.gray {
		color: #606266;
	}

	.ops-container {
		display: flex;
		flex-wrap: wrap;
		gap: 10px;

		.ops-grid {
			color: #4366e3;
			cursor: pointer;
			font-size: 12px;

			&:hover {
				text-decoration: underline;
			}

			&.delete {
				color: #F56C6C;
			}
		}
	}

	.custom-table {
		::v-deep {
			th {
				background: #f8f9fb;
				color: #333;
			}
		}
	}
</style>